import type {
    AgentMessageFragmentAction,
    AgentMessageFragmentFileModification,
    Message,
    UserMessage,
} from "@/hooks/use-stream-conversation";

type MessageWithActions = {
    userMessage: UserMessage | undefined;
    hasMoreUserMessages: boolean;
    actions: AgentMessageFragmentAction[];
    edits: Record<string, AgentMessageFragmentFileModification[]>;
};

/**
 * Finds a user message matching the predicate and collects all agent actions and file modifications that follow it
 * until the next user message is encountered.
 *
 * @param messages - Array of messages to process
 * @param predicate - Function that determines if a user message is the one we're looking for
 * @returns Object containing the matching user message, its associated actions, and file modifications
 */
export function findMessageWithActions(
    messages: Message[],
    predicate: (message: UserMessage) => boolean,
): MessageWithActions {
    let userMessage: UserMessage | undefined;
    const actions: AgentMessageFragmentAction[] = [];
    const edits: Record<string, AgentMessageFragmentFileModification[]> = {};

    for (const message of messages) {
        if (message.type === "user") {
            // If we already found our target message and see a new user message,
            // we're done collecting actions and edits
            if (userMessage) {
                return { userMessage, actions, edits, hasMoreUserMessages: true };
            }
            // Check if this is the message we're looking for
            if (predicate(message)) {
                userMessage = message;
            }
        } else if (message.type === "agent") {
            // Only collect actions and edits if we've found our target message
            if (!userMessage) {
                continue;
            }
            // Collect all action and file modification fragments from this agent message
            for (const fragment of message.fragments) {
                if (fragment.type === "action") {
                    actions.push(fragment);
                } else if (fragment.type === "fileModification") {
                    edits[fragment.filePath] = [...(edits[fragment.filePath] || []), fragment];
                }
            }
        }
    }

    return { userMessage, actions, edits, hasMoreUserMessages: false };
}
